'use client'

import MainMenu from '@/components/MainMenu';
import Footer from '@/components/Footer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight, faClock, faMoneyBill, faStar, faX } from '@fortawesome/free-solid-svg-icons';
import Image from 'next/image';
import { useCallback, useEffect, useState } from 'react';
import { CareerModal } from '@/components/CareerModal';
import usePhoneNumber from '@/hooks/usePhoneNumber';
import { sendRequestNoToken } from '@/utils/apiService';
import { salaryRanges, ShortCareer, timeCommitmentRanges } from '@/utils/types';
import Select from 'react-select';

const Page = () => {
    const [isCareerModalOpen, setCareerModalOpen] = useState(false);
    const { phoneNumber, isCookieLoading } = usePhoneNumber();
    const [careers, setCareers] = useState<ShortCareer[]>([]);
    const [filteredCareers, setFilteredCareers] = useState<ShortCareer[]>([]);
    const [selectedCareer, setSelectedCareer] = useState<ShortCareer | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedSalaryRanges, setSelectedSalaryRanges] = useState<{ min: number; max: number, label: string }[]>([]);
    const [selectedTimeCommitments, setSelectedTimeCommitments] = useState<number[]>([]);
    const [selectedStarCounts, setSelectedStarCounts] = useState<number[]>([]);

    const openCareerModal = (code: ShortCareer) => {
        setSelectedCareer(code);
        setCareerModalOpen(true);
    };

    const fetchData = useCallback(() => {
        setIsLoading(true);
        sendRequestNoToken("POST", `/api/careers/`, null)
            .then((data) => {
                setCareers(data.careers);
                setFilteredCareers(data.careers);
            })
            .catch((error) => {
                console.error("Error fetching data:", error);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);


    useEffect(() => {
        if (searchTerm === "" && selectedSalaryRanges.length === 0 && selectedTimeCommitments.length === 0) {
            setFilteredCareers(careers);
        } else {
            const lowerCaseSearchTerm = searchTerm.toLowerCase();
            setFilteredCareers(
                careers.filter((career) => {
                    const matchesSearchTerm =
                        career.name.toLowerCase().includes(lowerCaseSearchTerm) ||
                        career.objective.toLowerCase().includes(lowerCaseSearchTerm) ||
                        career.description.toLowerCase().includes(lowerCaseSearchTerm) ||
                        career.courses?.some((course) =>
                            course.name.toLowerCase().includes(lowerCaseSearchTerm) ||
                            course.schools.some((school) =>
                                school.name.toLowerCase().includes(lowerCaseSearchTerm)
                            )
                        );

                    const matchesSalaryRange = selectedSalaryRanges.length > 0
                        ? selectedSalaryRanges.some(range =>
                            career.avg_salary >= range.min && career.avg_salary <= range.max
                        )
                        : true;

                    const matchesTimeCommitment = selectedTimeCommitments.length > 0
                        ? selectedTimeCommitments.includes(career.time_commitment) ||
                        (selectedTimeCommitments.includes(Infinity) && career.time_commitment > 6)
                        : true;

                    return matchesSearchTerm && matchesSalaryRange && matchesTimeCommitment;
                })
            );
        }
    }, [searchTerm, careers, selectedSalaryRanges, selectedTimeCommitments]);


    const resetFilters = () => {
        setSearchTerm("");
        setSelectedSalaryRanges([]);
        setSelectedTimeCommitments([]);
        setFilteredCareers(careers);
    };


    if (isLoading) return (
        <div className="flex items-center justify-center min-h-screen bg-gray-100 dark:bg-dark-800">
            <div className="text-center">
                <div className="spinner-border animate-spin inline-block w-16 h-16 border-8 border-solid border-purple-500 border-t-transparent rounded-full"></div>
                <p className="mt-4 text-xl dark:text-dark-100 text-gray-700">Уншиж байна...</p>
            </div>
        </div>
    )


    return (
        <main className="flex min-h-screen flex-col items-center justify-between overflow-hidden">
            <CareerModal
                isOpen={isCareerModalOpen}
                onClose={() => { setCareerModalOpen(false) }}
                selectedCareer={null}
                uuid={null}
                careerCode={selectedCareer ? selectedCareer.code : null}
            />
            <MainMenu isLoading={isCookieLoading} phoneNumber={phoneNumber} scrollIndicatorEnabled={true} />
            <section className="text-start w-screen justify-start bg-gradient-start min-h-screen dark:bg-dark-800">
                <div className='container mt-28 mb-8 text-center'>
                    <div>
                        <div className='text-4xl md:text-6xl font-semibold'>
                            my<span className='text-purple-500'>Job</span>
                        </div>
                        <div className='text-lg mt-10 max-w-[600px] mx-auto'>
                            Төрөл бүрийн ажил мэргэжлийн тодорхойлолт, гүйцэтгэх үүрэг, дундаж цалингийн талаарх мэдээлэл.
                        </div>
                    </div>
                </div>
                <div className="">
                    <div className="space-y-6">
                        <div className='container'>
                            <div className='mt-2 grid grid-cols-1 md:grid-cols-2  gap-y-2 gap-x-2  text-xs'>
                                <Select
                                    isSearchable={false}
                                    isMulti
                                    value={selectedSalaryRanges.map(range => ({ value: range.label, label: range.label, min: range.min, max: range.max }))}
                                    onChange={(options) => {
                                        setSelectedSalaryRanges(options ? options.map(option => ({ min: option.min, max: option.max, label: option.label })) : []);
                                    }}
                                    options={salaryRanges.map(range => ({ value: range.label, label: range.label, min: range.min, max: range.max }))}
                                    placeholder="Дундаж цалин"
                                    className="rounded"
                                    styles={{
                                        control: (provided, state) => ({
                                            ...provided,
                                            borderRadius: '0.5rem',
                                            padding: '4px',
                                            borderColor: state.isFocused ? '#8646f4' : '#afafaf',
                                            boxShadow: state.isFocused ? '0 0 0 1px #3182ce' : 'none',
                                            '&:hover': {
                                                borderColor: '#8646f4',
                                            },
                                        }),
                                        input: (provided) => ({
                                            ...provided,
                                            padding: '4px',
                                        }),
                                        placeholder: (provided) => ({
                                            ...provided,
                                            color: '#718096',
                                        }),
                                        singleValue: (provided) => ({
                                            ...provided,
                                            color: '#111928',
                                        }),
                                        menu: (provided) => ({
                                            ...provided,
                                            backgroundColor: 'white',
                                            color: '#111',
                                        }),
                                        menuList: (provided) => ({
                                            ...provided,
                                            padding: '0',
                                        }),
                                    }}
                                />

                                <Select
                                    isSearchable={false}
                                    isMulti
                                    value={selectedTimeCommitments.map(value => ({ value, label: `${value} жил` }))} // Adjust label based on your needs
                                    onChange={(options) => {
                                        setSelectedTimeCommitments(options ? options.map(option => option.value) : []);
                                    }}
                                    options={timeCommitmentRanges.map(range => ({ value: range.value, label: range.label }))}
                                    placeholder="Бэлтгэх хугацаа"
                                    className="rounded"
                                    styles={{
                                        control: (provided, state) => ({
                                            ...provided,
                                            borderRadius: '0.5rem',
                                            padding: '4px',
                                            borderColor: state.isFocused ? '#8646f4' : '#afafaf',
                                            boxShadow: state.isFocused ? '0 0 0 1px #3182ce' : 'none',
                                            '&:hover': {
                                                borderColor: '#8646f4',
                                            },
                                        }),
                                        input: (provided) => ({
                                            ...provided,
                                            padding: '4px',
                                        }),
                                        placeholder: (provided) => ({
                                            ...provided,
                                            color: '#718096',
                                        }),
                                        singleValue: (provided) => ({
                                            ...provided,
                                            color: '#111928',
                                        }),
                                        menu: (provided) => ({
                                            ...provided,
                                            backgroundColor: 'white',
                                            color: '#111',
                                        }),
                                        menuList: (provided) => ({
                                            ...provided,
                                            padding: '0',
                                        }),
                                    }}
                                />
                            </div>
                            <div className='mt-2 flex flex-row space-x-2'>
                                <input
                                    type="text"
                                    placeholder="Түлхүүр үг"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full p-3 text-xs border rounded-lg border-gray-600 shadow-sm outline-purple-500 placeholder:text-dark-400 text-dark-700 "
                                />
                                <button
                                    onClick={resetFilters}
                                    className="flex items-center justify-center bg-career-dark-200 text-white px-5 rounded-lg"
                                >
                                    <FontAwesomeIcon icon={faX} />
                                </button>
                            </div>
                        </div>


                        <div className='' style={{ scrollMarginTop: '6rem' }} id='career'>
                            <div className='grid grid-cols-1 divide-y bg-white md:bg-transparent md:grid-cols-3 md:container md:divide-none mt-6 md:gap-x-5 md:gap-y-5 mb-28'>
                                {isLoading ? (
                                    Array.from({ length: 6 }).map((_, index) => (
                                        <div key={index} className='container items-center flex flex-row justify-between space-x-2 md:bg-white md:rounded-md p-0 overflow-hidden'>
                                            <div className='flex-shrink-0 h-[120px] w-[120px] bg-gray-300 animate-pulse'></div>
                                            <div className='flex-auto text-xs flex flex-col m-auto'>
                                                <div className='h-4 bg-gray-300 rounded w-1/2 animate-pulse'></div>
                                                <div className='mt-2 h-4 bg-gray-300 rounded w-3/4 animate-pulse'></div>
                                                <div className='mt-2 h-4 bg-gray-300 rounded w-1/3 animate-pulse'></div>
                                            </div>
                                            <div className='text-gray-500 pe-4 py-4'>
                                                <FontAwesomeIcon icon={faChevronRight}></FontAwesomeIcon>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    filteredCareers.map((item, index) => (
                                        <button key={index} onClick={() => openCareerModal(item)}>
                                            <div className='container items-center flex flex-row justify-between space-x-2 md:bg-white md:rounded p-0 overflow-hidden dark:bg-dark-600'>
                                                <div className='flex-shrink-0 h-[120px] w-[120px] bg-gray-400'>
                                                    <Image src={item.icon} width={120} height={120} alt={''} />
                                                </div>
                                                <div className='flex-auto text-sm flex flex-col m-auto'>
                                                    <div className='line-clamp-2 text-start font-semibold'>{item.name}</div>
                                                    <div className='mt-1 text-start text-xs'>{item.time_commitment ? (<>Бэлтгэх хугацаа: {item.time_commitment} жил</>) : (<>Бэлтгэл шаардлагагүй</>)}</div>
                                                    {item.avg_salary ? (<div className='text-start text-xs '>Дундаж цалин: {item.avg_salary.toLocaleString()}₮</div>) : (<></>)}
                                                </div>
                                                <div className='text-gray-500 pe-4 py-4'>
                                                    <FontAwesomeIcon icon={faChevronRight}></FontAwesomeIcon>
                                                </div>
                                            </div>
                                        </button>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            <Footer showNavigation={false} />
        </main>
    );
};

export default Page;

